import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { App, AppStatus, AuthenticationError, AuthorizationError, CustomDomainStatus, DisposableEmailError, NetworkError, NotFoundError, User } from '@back4app2/sdk';
import { BACK4APP_DOT_COM_SITE_URL, BACK4APP_DOT_COM_DASHBOARD_URL } from '../settings';
import back4app2 from '../back4app2';
import LoadingSpinner from '../components/LoadingSpinner';
import { ReactComponent as LinkIconSVG } from '../assets/images/link-icon.svg';
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import ErrorMessage from '../components/ErrorMessage';
import MyAppActions from '../components/MyAppActions';
import TemporaryUrlNotice from '../components/TemporaryUrlNotice';
import FirstDeployModal from '../components/FirstDeployModal';
import PaymentPastDueNotice from '../components/PaymentPastDueNotice';
import { toast } from 'react-hot-toast';
import {
  BottomSheet, DsButton, Eyebrow, ModalDialog, SettingsFooter, SettingsSaveProvider, SheetGroup, SheetRows, StatusDot,
} from '../components/ds';
import type { SettingsSaveState, SheetRow } from '../components/ds';
import { useAppActionRows } from '../components/MyAppActions';
import { ReactComponent as TriangleExclamationIcon } from '../assets/images/triangle-exclamation.svg';
import {
  ArrowLeftIcon, BranchIcon, DatabaseIcon, DeploymentsIcon, FileIcon, LogsIcon,
  MetricsIcon, MoreHorizontalIcon, OverviewIcon, SettingsIcon, UploadIcon,
} from '../components/ds/icons';
import { AppStatusTone, getAppStatusSummary } from '../utils/appStatus';
import { appSourceKind } from '../utils/appSource';
import { timeFromNow } from '../utils/dates';
import { canReadSource } from '../utils/permissions';
import { getVisibleSettingsTabs, upgradePath } from './MyAppSettings';
import type { SettingsFocus } from '../utils/settingsFocus';

interface MyAppState {
  isLoading: boolean;
  loadingErrorMessage?: string;
  app?: App;
  appLoadedAt?: Date;
  isDeletingApp: boolean;
}

const INITIAL_STATE: MyAppState = {
  isLoading: true,
  isDeletingApp: false
};

enum MyAppActionType {
  RESET,
  FINISH_LOADING,
  SET_IS_DELETING_APP
}

const reset = () => ({
  type: MyAppActionType.RESET
} as const);

const finishLoading = (errorMessage?: string, app?: App) => ({
  type: MyAppActionType.FINISH_LOADING,
  payload: {
    errorMessage,
    app
  }
} as const);

const setIsDeletingApp = (isDeleting: boolean) => ({
  type: MyAppActionType.SET_IS_DELETING_APP,
  payload: isDeleting
} as const);

type MyAppAction = ReturnType<typeof reset> | ReturnType<typeof finishLoading> | ReturnType<typeof setIsDeletingApp>;

const reducer = (state: MyAppState = INITIAL_STATE, action: MyAppAction): MyAppState => {
  switch (action.type) {
    case MyAppActionType.RESET:
      return {
        ...INITIAL_STATE,
      };
    
    case MyAppActionType.FINISH_LOADING: {
      if (action.payload.errorMessage === 'App not found.' && state.isDeletingApp) {
        return {
          ...state,
          isLoading: false,
          appLoadedAt: new Date()
        }
      }
      return {
        ...state,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        app: action.payload.app,
        appLoadedAt: new Date()
      }
    }
    
    case MyAppActionType.SET_IS_DELETING_APP:
      return {
        ...state,
        isDeletingApp: action.payload
      }
  }
};

// The phone header's status line: the state's own colour, as the sidebar's
// pill has it, but as text with a dot — a pill beside the name was a second
// button-shaped thing in a strip that now has one.
const STATUS_TEXT: Record<AppStatusTone, string> = {
  ok: 'text-state-ok-soft',
  error: 'text-state-err',
  warn: 'text-state-warn',
  info: 'text-action-soft',
  idle: 'text-navy-200',
};
const STATUS_DOT: Record<AppStatusTone, string> = {
  ok: 'bg-state-ok-soft',
  error: 'bg-state-err',
  warn: 'bg-state-warn',
  info: 'bg-action-soft',
  idle: 'bg-navy-300',
};

/**
 * The phone header's "⋯": what the open screen can do (its rows first),
 * the app's address, then the Action menu's rows. Its own component so the
 * deploy hook runs only once the app is loaded.
 */
const AppSheet = ({ app, appUrl, logsUrl, screenLabel, screenRows, open, onClose }: {
  app: App,
  appUrl?: string,
  /** The deployment's logs: where a failed or running deploy is explained. */
  logsUrl: string,
  /** The open screen, naming its block of rows. */
  screenLabel: string,
  screenRows: SheetRow[],
  open: boolean,
  onClose: () => void,
}) => {
  const navigate = useNavigate();
  const { rows: actionRows, modals } = useAppActionRows(app);
  // Two blocks: what the open screen does, then the app. Few rows in each —
  // the sheet lists what is done often from a phone; a rollback is a
  // decision made from the Deployments list.
  const appRows: SheetRow[] = [
    { label: 'View logs', onClick: () => navigate(logsUrl) },
    ...(appUrl
      ? [{
          label: 'Open app URL',
          meta: appUrl.split('.')[0],
          onClick: () => window.open(`${window.location.protocol}//${appUrl}`, '_blank', 'noreferrer'),
        }]
      : []),
    ...actionRows.slice(0, 1),
  ];
  return <>
    <BottomSheet open={open} onClose={onClose} title={app.name}>
      {screenRows.length ? (
        <SheetGroup label={screenLabel}>
          <SheetRows rows={screenRows} onPick={onClose} />
        </SheetGroup>
      ) : null}
      <SheetGroup label="app">
        <SheetRows rows={appRows} onPick={onClose} />
      </SheetGroup>
    </BottomSheet>
    {modals}
  </>;
};

const MyApp = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  /**
   * Settings' own section menu, opened by the Settings tab itself.
   *
   * It lives up here because the trigger is in this component's tab bar and the
   * panel is down in MyAppSettings; the strip that used to carry the trigger
   * sat directly above the bar repeating the word it already said.
   */
  const [isSectionMenuOpen, setIsSectionMenuOpen] = useState(false);
  // The phone header's "⋯": the app's actions, and whatever the open screen
  // adds to them (the Source Code screen puts its file actions there).
  const [isAppSheetOpen, setIsAppSheetOpen] = useState(false);
  const [screenActions, setScreenActions] = useState<SheetRow[]>([]);
  /** Published by whichever Settings form is mounted; drawn by the footer. */
  const [saveState, setSaveState] = useState<SettingsSaveState | null>(null);
  /** The section a confirmation is standing between the customer and. */
  const [pendingSettingsTab, setPendingSettingsTab] = useState<string | null>(null);

  const { isLoading, loadingErrorMessage, app, appLoadedAt } = state;
  const mainRef = useRef<HTMLDivElement>(null);
  const { user, freePlanUsageHours } = useOutletContext<{ user: User, freePlanUsageHours: number }>();

  useEffect(
    () => {
      const subscription = back4app2.subscribeToApp(appId as string, (error, snapshot) => {
        if (error) {
          if (error instanceof NetworkError) {
            console.error('network error', error);
            dispatch(finishLoading('Network error when loading app. Check your internet connection and try again.'));
          } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
            window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
          } else if (error instanceof NotFoundError) {
            dispatch(finishLoading('App not found.'));
          } else {
            console.error('unexpected error loading app', error);
            dispatch(finishLoading('Unexpected error when loading app. Please try again.'));
          }
        } else {
          dispatch(finishLoading(undefined, snapshot));
        }        
      });

      return () => {
        subscription.unsubscribe();
        dispatch(reset());
      }
    },
    [appId]
  );

  const updateIsDeletingApp = (isDeleting: boolean) => dispatch(setIsDeletingApp(isDeleting));

  useEffect(() => {
    if (app?.status === AppStatus.PENDING_VERIFICATION && app?.error && app.error.code === DisposableEmailError.CODE) {
      toast.error(app.error.message, {
        className:`bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-3xl text-sm`,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in flex-none" />,
        duration: 5000
      });
    }
  }, [app])

  if (isLoading) {
    return (<>
      <div className="grow flex flex-col justify-center items-center">
        <LoadingSpinner />
        <div className="mt-4 text-ds-135 text-navy-200">
          Loading app
        </div>
      </div>
    </>);
  }

  if (loadingErrorMessage) {
    return (<>
      <ErrorMessage message={loadingErrorMessage} />
    </>);
  }

  const env = app!.mainService && app!.mainService.mainServiceEnvironment;
  const customDomain = env && env.mainCustomDomain;
  const plan = env && env.plan;
  const planRAM = plan?.maxRAM
    ? (plan.maxRAM >= 1024 ? `${plan.maxRAM / 1024} GB` : `${plan.maxRAM} MB`)
    : undefined;

  // Which route built the app. The SDK asks for `sourceKind` rather than a
  // truthiness test on `repository`, but the field arrived after some apps
  // did — where it is missing, fall back to the old test.
  const sourceKind = env ? appSourceKind(env, !!app!.mainService?.repository) : 'GITHUB';
  // Source Code is a place only for an upload app, and only for someone who
  // may read its code: a collaborator with just Overview and Logs is not.
  const hasSourceScreen = sourceKind === 'UPLOAD' && !!app && canReadSource(app);

  // Every destination named once. `hash` is for the two Settings tabs promoted
  // into the menu — they are sections of that screen, not routes of their own,
  // and MyAppSettings already switches on the browser hash.
  const NAV: Record<string, NavDestination> = {
    overview: { label: 'Overview', short: 'Overview', Icon: OverviewIcon, url: `/apps/${appId}` },
    // "Runtime Logs", because the screen is now only that: the build half
    // moved out to Deployments, where a build log belongs to the deploy that
    // produced it. The bottom bar keeps the short "Logs" — two words do not
    // fit a phone tab.
    logs: { label: 'Runtime Logs', short: 'Logs', Icon: LogsIcon, url: `/apps/${appId}/logs` },
    metrics: { label: 'App Metrics', short: 'Metrics', Icon: MetricsIcon, url: `/apps/${appId}/metrics` },
    // Not under Settings: the database is not a setting of the app, it is a
    // place in it — the one screen here that shows the app's own data rather
    // than the platform's record of the app.
    database: { label: 'Database', short: 'Data', Icon: DatabaseIcon, url: `/apps/${appId}/database` },
    deployments: { label: 'Deployments', short: 'Deploys', Icon: DeploymentsIcon, url: `/apps/${appId}/deployments` },
    // Upload apps only: their code lives here. A GitHub app's is on GitHub,
    // and an image app has none of its own.
    source: { label: 'Source Code', short: 'Code', Icon: FileIcon, url: `/apps/${appId}/source` },
    settings: { label: 'Settings', short: 'Settings', Icon: SettingsIcon, url: `/apps/${appId}/settings` },
  };

  /**
   * What the app is doing, then how it is set up.
   *
   * The first group is the four routes. Below the rule, every section of
   * Settings, under a SETTINGS eyebrow — which is the one place a word beats a
   * rule here, because those six rows all lead to the same screen and the
   * eyebrow is what says so.
   *
   * Before this there was a single Settings row plus two of its sections
   * promoted up beside it — Env variables and Domains — chosen because they
   * were the two people asked for. That left four sections reachable only
   * through a strip of pills inside the screen, so the menu could take you to
   * Settings but the strip is what actually moved you around it: a second
   * menu, inside a screen the first menu had already opened, that scrolled
   * out of view on a narrow window. All six are rows now, and the strip is
   * gone.
   */
  const routeGroup = [
    NAV.overview,
    NAV.deployments,
    ...(hasSourceScreen ? [NAV.source] : []),
    NAV.database,
    NAV.logs,
    NAV.metrics,
  ];

  // The bottom bar keeps four, plus More. It held six, then seven once an
  // upload app's Source Code joined, and seven across 360px left tabs 35px
  // wide — under a thumb, and easy to land on the neighbour. The four are the
  // ones watched while an app runs; the rest are places visited on purpose,
  // and More lists them in the same sheet as the Settings sections.
  const tabSections = [
    NAV.overview,
    NAV.deployments,
    NAV.logs,
    NAV.metrics,
  ];
  const morePlaces = [
    ...(hasSourceScreen ? [NAV.source] : []),
    NAV.database,
  ];

  // The sidebar's "All apps" is a destination — it leaves the containers
  // dashboard for the account-wide list, and says so.
  const goToAllApps = () => { window.location.href = `${BACK4APP_DOT_COM_DASHBOARD_URL}/apps`; };

  const isOverviewRoute = pathname === `/apps/${appId}` || pathname === `/apps/${appId}/`;

  /**
   * The arrow in the mobile header, by where it is pressed.
   *
   * On the Overview it leaves the app: the Overview is the app's own front
   * door, so the only place above it is My Apps on the dashboard — never this
   * dashboard's older app list, which is where a bare `/apps` lands and what
   * made the destination look random.
   *
   * In a section — Deployments, Logs, Metrics, Settings — it is a back button
   * and goes back a page. React Router stamps an `idx` on history state;
   * anything above zero means we arrived here from a screen of our own. At
   * zero there is no previous screen — a deep link, a fresh tab — and the
   * section's own Overview is what is above it.
   */
  const goBack = () => {
    if (isOverviewRoute) {
      goToAllApps();
      return;
    }

    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;

    if (idx > 0) {
      navigate(-1);
    } else {
      navigate(`/apps/${appId}`);
    }
  };

  const isImageSource = sourceKind === 'DOCKER_HUB';

  /**
   * What the app is built from, in one line under its name. The artboard gives
   * it no label: at this size the image reference is self-evidently an image
   * reference, and a rule above it would only push the status card down.
   */
  const sourceLine = env ? (isImageSource ? (
    <span className="block font-courrier text-ds-115 text-navy-300 truncate">
      {env.imageRepository}:{env.imageTag}
    </span>
  ) : sourceKind === 'UPLOAD' ? (
    <span className="flex items-center gap-1.5 min-w-0 font-courrier text-ds-115 text-navy-300">
      <UploadIcon size={12} />
      <span className="truncate">Uploaded files</span>
    </span>
  ) : (
    <span className="flex flex-col gap-1 min-w-0">
      {app!.mainService?.repository ? (
        <a
          target="_blank"
          rel="noreferrer"
          href={`https://www.github.com/${app!.mainService.repository.fullName}`}
          className="b4-focus font-courrier text-ds-115 text-navy-300 hover:text-action-soft truncate"
        >
          {app!.mainService.repository.fullName}
        </a>
      ) : null}
      <span className="flex items-center gap-1.5 min-w-0 font-courrier text-ds-115 text-navy-300">
        <BranchIcon size={12} />
        <span className="truncate">{env.branchName}</span>
      </span>
    </span>
  )) : null;

  /**
   * The address the app is served on.
   *
   * A blocked app must not advertise a live address. The URL is hidden
   * wherever it appears while the security review is open — here, in the app
   * list, and in the deploy summary on the overview.
   */
  const appUrl = env
    && customDomain
    && customDomain.status !== CustomDomainStatus.EXPIRED
    && !app!.block
    ? customDomain.name
    : undefined;

  const appUrlHref = appUrl ? `${window.location.protocol}//${appUrl}` : undefined;

  // The address reads as the address. That a free plan's is temporary is
  // `TemporaryUrlNotice`'s to say, a few rows below in this same sidebar,
  // where it has room for the term and the way out.
  const appUrlLine = appUrl ? (
    <a
      target="_blank"
      rel="noreferrer"
      href={appUrlHref}
      className="b4-focus inline-flex items-center gap-1.5 min-w-0 font-courrier text-ds-115 text-state-ok-soft hover:text-white"
    >
      <span className="truncate">{appUrl}</span>
      <LinkIconSVG className="flex-none" />
    </a>
  ) : null;

  /**
   * The state of the app, as the one card the sidebar leads with. It reads and
   * does nothing: the deploy that used to hang off it as a menu is the button
   * under it now, which says what it ships.
   */
  const settingsTabs = getVisibleSettingsTabs(app);
  const isSettingsRoute = pathname.startsWith(`/apps/${appId}/settings`);
  const isOnPath = (url: string) => pathname === url || pathname === `${url}/`;
  const isMoreRoute = isSettingsRoute || morePlaces.some(place => isOnPath(place.url));
  const activeSettingsTab = isSettingsRoute
    ? (settingsTabs.find(tab => tab.id === hash.replace('#', '')) || settingsTabs[0])
    : undefined;

  /**
   * Switching sections unmounts the form, and an unmounted form's edits are
   * gone. It used to happen in silence; now it asks.
   */
  const goToSettingsTab = (id: string, focus?: SettingsFocus) => {
    if (saveState && saveState.dirtyCount > 0 && isSettingsRoute && id !== activeSettingsTab?.id) {
      setPendingSettingsTab(id);
      return;
    }
    // The field asked about rides in the history entry, not the URL: it is a
    // pointer for this arrival, and a reload or a shared link should not
    // point again.
    navigate(`/apps/${appId}/settings#${id}`, focus ? { state: { settingsFocus: focus } } : undefined);
  };

  const status = getAppStatusSummary(app!);
  // When the state was reached: the last deployment's age, as the Overview
  // reads it beside the same word on the desktop.
  const lastDeploymentAt = env && env.lastDeployment ? env.lastDeployment.createdAt : undefined;

  // The phone header's screen name: the route, or the Settings section.
  const screenLabel = isSettingsRoute
    ? activeSettingsTab?.label || NAV.settings.label
    : (routeGroup.find(item => isOnPath(item.url)) || NAV.overview).label;

  /**
   * The state, as a line rather than as a card.
   *
   * It carried the age of the last deploy under it and was boxed to hold the
   * pair. The age is written twice on the Overview already — beside the
   * heading and on the deployment line over the log — and once it went, what
   * was left was a bordered panel around a single word, heavier than the
   * address and the branch above it and saying less. It joins them.
   *
   * It stays in the sidebar rather than deferring to the Overview's heading:
   * Logs, Deployments and Settings put no status in their own heads, and this
   * is the only reading of it those screens have.
   */
  const statusLine = (
    <span className="flex items-center gap-2 min-w-0">
      <StatusDot tone={status.tone} halo />
      <span className="min-w-0 font-sora text-ds-135 font-semibold tracking-[-0.01em] text-white truncate">
        {status.text}
      </span>
    </span>
  );

  return (<>
    {/* In the shell rather than on the Overview: the deploy can finish while
        the customer is reading the build log on Deployments. */}
    <FirstDeployModal app={app!} />
    <div className="flex-1 min-h-0 flex bg-navy-900">

      {/* ── Sidebar: desktop only. On mobile it becomes the context header
             plus the bottom tab bar below. ─────────────────────────────── */}
      <aside className="hidden lg:flex flex-none w-[300px] [--b4-type:1] bg-navy-800 border-r border-navy-700 flex-col overflow-y-auto b4-thin">
        <div className="px-3 py-5 border-b border-navy-700 flex flex-col gap-4">
          {/* "All apps" owns its row. */}
          <div className="px-1">
            <button
              type="button"
              onClick={goToAllApps}
              className="b4-focus inline-flex items-center gap-1.5 rounded-control px-1 py-0.5 -ml-1 text-[length:calc(13px*var(--b4-type,1))] font-medium text-navy-200 hover:text-white transition-colors duration-[120ms]"
            >
              <ArrowLeftIcon size={14} />
              All apps
            </button>
          </div>

          <div className="px-1 flex flex-col gap-2 min-w-0">
            <Eyebrow>container</Eyebrow>
            <div
              className="font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white truncate"
              title={app!.name}
            >
              {app!.name}
            </div>
            {sourceLine}
            {appUrlLine}
          </div>

          {/* State, then the one thing to do about it. The action is a button
              of its own rather than a menu hidden behind the card: it is the
              only action this app has, and a blocked app — which has no deploy
              to offer — simply ends at the card. */}
          {/* The reading first, then what can be done about it: the state is
              why anyone reaches for the menu, so it is read before the menu is
              seen. Redeploy is not here — it belongs to the expired state, and
              the notice draws it beside the offer it competes with.

              `gap-4` rather than `gap-3`, which is the outer block's own
              rhythm: at 12px the status sat close enough to the menu to read
              as its label. */}
          <div className="px-1 flex flex-col gap-4">
            {statusLine}
            <MyAppActions app={app!} />
            <TemporaryUrlNotice app={app!} />
            {/* Not on Settings: the legacy plan change there runs its own
                Paddle checkout, and Paddle keeps one event callback per page. */}
            {isSettingsRoute ? null : (
              <PaymentPastDueNotice app={app!} onUpdated={() => window.location.reload()} />
            )}
          </div>
        </div>

        <div className="px-2 py-4 flex flex-col">
          <div className="flex flex-col gap-1">
            {routeGroup.map(item => (
              <MenuItem key={item.url} Icon={item.Icon} title={item.label} url={item.url} />
            ))}
          </div>

          {/* The eyebrow is indented to the label, not to the icon: it names
              the rows, and the rows are read down their text. */}
          <div className="mt-4 pt-4 border-t border-navy-700 flex flex-col gap-1">
            <div className="px-2.5 pb-1"><Eyebrow>settings</Eyebrow></div>
            {settingsTabs.map(tab => (
              <MenuItem
                key={tab.id}
                Icon={tab.Icon}
                title={tab.label}
                url={NAV.settings.url}
                hash={`#${tab.id}`}
                count={tab.count}
                // Landing on /settings with no hash shows the first section,
                // so that row has to light up for a hash it does not carry.
                isActive={isSettingsRoute && activeSettingsTab?.id === tab.id}
                // Through the guard, not straight to the href: switching
                // sections unmounts the form, and the strip these rows replace
                // asked before throwing away unsaved edits. It stays a real
                // link underneath so the row is still middle-clickable.
                onNavigate={() => goToSettingsTab(tab.id)}
              />
            ))}
          </div>
        </div>

        {plan ? (
          <div className="mt-auto px-4 pt-4 pb-5 border-t border-navy-700 flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">{plan.name}</span>
              <button
                type="button"
                onClick={() => navigate(upgradePath(appId as string))}
                className="b4-focus rounded-control text-ds-125 font-semibold text-action-soft hover:text-white"
              >
                Upgrade
              </button>
            </div>
            <span className="font-courrier text-ds-115 leading-[1.5] text-navy-200">
              {[planRAM, plan.maxCPU ? `${plan.maxCPU} vCPU` : undefined].filter(Boolean).join(' · ')}
            </span>
          </div>
        ) : null}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">

        {/* ── Mobile context header ──────────────────────────────────────── */}
        {/* The arrow carries no label because at this width there is none to
            spare — it goes on the button's `aria-label`, which says which of
            the two things the arrow currently is. */}
        {/* Sideways it collapses to one row. The CONTAINER eyebrow says
            nothing about this app in particular, so it is what a short screen
            spends first. */}
        <div className="lg:hidden flex-none bg-navy-800 border-b border-navy-500">
          <div className="px-4 pt-2 pb-2 short:py-1.5 flex items-center gap-2.5">
            <button
              type="button"
              aria-label={isOverviewRoute ? 'All apps' : 'Go back'}
              onClick={goBack}
              className="b4-focus w-10 h-10 short:w-9 short:h-9 flex-none -ml-2.5 rounded-control text-navy-200 flex items-center justify-center hover:bg-navy-600"
            >
              <ArrowLeftIcon size={18} />
            </button>
            {/* Where you are, whose app, and how it is: the eyebrow names the
                screen (the tab bar's job on the desktop is the sidebar's),
                the name is the name, and the state is a line under it in its
                own colour. The address and the Action button left the strip
                for the "⋯" on the right, which holds everything that can be
                done from here. */}
            <div className="flex-1 min-w-0 flex flex-col short:flex-row short:items-baseline short:gap-2.5">
              <span className="short:order-2"><Eyebrow>{screenLabel}</Eyebrow></span>
              <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white truncate short:order-1 short:flex-1 short:min-w-0">
                {app!.name}
              </span>
              <span className="mt-0.5 short:mt-0 short:order-3 flex items-center gap-1.5 min-w-0 font-courrier text-ds-105 uppercase tracking-[0.08em]">
                <span className={`w-1.5 h-1.5 flex-none rounded-full ${STATUS_DOT[status.tone]}`} />
                <span className={`truncate ${STATUS_TEXT[status.tone]}`}>{status.text}</span>
                {lastDeploymentAt ? (
                  <span className="truncate normal-case tracking-normal text-navy-300">· {timeFromNow(lastDeploymentAt)}</span>
                ) : null}
              </span>
            </div>
            <button
              type="button"
              aria-label="App actions"
              aria-haspopup="dialog"
              aria-expanded={isAppSheetOpen}
              onClick={() => setIsAppSheetOpen(open => !open)}
              className="b4-focus w-10 h-10 short:w-9 short:h-9 flex-none -mr-2.5 rounded-control text-navy-100 flex items-center justify-center hover:bg-navy-600"
            >
              <MoreHorizontalIcon size={18} />
            </button>
          </div>
        </div>

        {/* The bottom padding clears two things that float over this region
            and belong to neither the page nor the scroll: the tab bar on a
            phone, and the support bubble, which is fixed bottom-right at every
            width. At `lg:pb-10` the bubble sat on top of the last row of
            whatever screen was open — the pager of the data grid, the newest
            line of the log — which is the row the reader is usually reaching
            for. */}
        <main
          className="flex-1 min-h-0 overflow-y-auto b4-thin px-4 pt-5 pb-24 lg:px-8 lg:pt-8 lg:pb-24 flex flex-col gap-5 lg:gap-6 [&>*]:min-w-0"
          ref={mainRef}
        >
          <SettingsSaveProvider value={setSaveState}>
            <Outlet context={{ app, appLoadedAt, mainRef, user, freePlanUsageHours, updateIsDeletingApp, saveState, goToSettingsTab, setScreenActions }} />
          </SettingsSaveProvider>
        </main>

        {/* ── Mobile tab bar. Four places and More, which opens the sheet
               with the rest. ─────────────────────────────────────────────── */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-navy-800 border-t border-navy-500 px-1 flex [padding-bottom:env(safe-area-inset-bottom)]">
          {tabSections.map(section => (
            <TabItem
              key={section.url}
              Icon={section.Icon}
              title={section.short}
              url={section.url}
              // While the sheet is open, More is where you are.
              isActive={isOnPath(section.url) && !isSectionMenuOpen}
            />
          ))}
          {/* Lit while one of its places is showing, so the bar always says
              where you are. Tapping it shows the list and waits — picking one
              is what navigates. */}
          <TabItem
            Icon={MoreHorizontalIcon}
            title="More"
            url={NAV.settings.url}
            isActive={isMoreRoute || isSectionMenuOpen}
            onOpensMenu={() => setIsSectionMenuOpen(prev => !prev)}
          />
        </nav>
      </div>
    </div>

    <AppSheet
      app={app!}
      appUrl={appUrl}
      logsUrl={NAV.deployments.url}
      screenLabel={screenLabel}
      screenRows={screenActions}
      open={isAppSheetOpen}
      onClose={() => setIsAppSheetOpen(false)}
    />

    {/* Chrome of the shell, not of one screen: the section menu has to be able
        to open over Overview or Logs, where MyAppSettings is not mounted. */}
    <SettingsFooter
      saveState={saveState}
      places={morePlaces.map(place => ({ label: place.label, url: place.url, Icon: place.Icon, isActive: isOnPath(place.url) }))}
      tabs={settingsTabs.map(tab => ({ id: tab.id, label: tab.label, count: tab.count, danger: tab.danger }))}
      activeTabId={activeSettingsTab?.id ?? ''}
      onSelectTab={goToSettingsTab}
      isOpen={isSectionMenuOpen}
      onClose={() => setIsSectionMenuOpen(false)}
    />

    <ModalDialog
      open={!!pendingSettingsTab}
      onClose={() => setPendingSettingsTab(null)}
      icon={<TriangleExclamationIcon className="text-state-err" width="20px" height="20px" />}
      title="Unsaved changes"
      blurb={`You have ${saveState?.dirtyCount ?? 0} unsaved change${saveState?.dirtyCount === 1 ? '' : 's'} in this section. Leaving discards ${saveState?.dirtyCount === 1 ? 'it' : 'them'}.`}
      footer={<>
        <DsButton onClick={() => setPendingSettingsTab(null)}>Stay here</DsButton>
        <DsButton
          variant="danger"
          onClick={() => {
            const target = pendingSettingsTab;
            setPendingSettingsTab(null);
            if (target) {
              navigate(`/apps/${appId}/settings#${target}`);
            }
          }}
        >
          Discard and leave
        </DsButton>
      </>}
    />
  </>);
};

/** A row of the menu. `short` is the label the mobile tab bar has room for. */
interface NavDestination {
  label: string;
  short: string;
  Icon: (props: { size?: number, className?: string }) => JSX.Element;
  url: string;
  hash?: string;
}

interface NavItemProps {
  Icon: (props: { size?: number, className?: string }) => JSX.Element;
  title: string;
  url: string;
  /** Set for the Settings sections that have their own row in the menu. */
  hash?: string;
}

/** Overview is the index route, so it only matches exactly. */
const useIsActive = (url: string) => {
  const { pathname } = useLocation();

  return pathname === url || pathname === `${url}/`;
};

/**
 * `isActive` is passed in for the Settings rows rather than derived here: the
 * shell already resolves which section is showing — including the empty hash,
 * which means the first one — and two answers to that question would be two
 * things to keep in step.
 */
const MenuItem = ({ Icon, title, url, hash, count, isActive: isActiveProp, onNavigate }: NavItemProps & {
  count?: number,
  isActive?: boolean,
  onNavigate?: () => void,
}) => {
  const isActiveByPath = useIsActive(url);
  const isActive = isActiveProp ?? isActiveByPath;

  return (
    <Link
      to={`${url}${hash || ''}`}
      onClick={onNavigate ? (event) => {
        // Plain clicks only: cmd/ctrl/shift/middle still open the href the way
        // the browser would, and those open a new context where there is no
        // form to lose.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }
        event.preventDefault();
        onNavigate();
      } : undefined}
      className={[
        'b4-focus flex items-center gap-2.5 h-[38px] px-2.5 rounded-control text-ds-135',
        'transition-colors duration-[120ms]',
        isActive ? 'bg-navy-600 text-white font-semibold' : 'text-navy-200 font-medium hover:text-white hover:bg-navy-600/50',
      ].join(' ')}
    >
      {/* A step brighter than the label at rest. A 1px line carries less ink
          than the letters beside it at the same colour, so matching them made
          the icon the faint half of a row that is one thing. Active is white
          throughout, and hover takes the whole row, so this only lifts the
          resting state. */}
      <Icon size={20} className={isActive ? '' : 'text-navy-100'} />
      <span className="flex-1 min-w-0 truncate">{title}</span>
      {count ? (
        <span className="flex-none font-courrier text-ds-105 text-navy-300">{count}</span>
      ) : null}
    </Link>
  );
};

const TabItem = ({ Icon, title, url, isActive, onOpensMenu }: NavItemProps & {
  isActive: boolean,
  onOpensMenu?: () => void,
}) => {
  return (
    <Link
      to={url}
      aria-haspopup={onOpensMenu ? 'menu' : undefined}
      onClick={onOpensMenu ? (event) => { event.preventDefault(); onOpensMenu(); } : undefined}
      data-settings-menu-trigger={onOpensMenu ? '' : undefined}
      className={[
        // Five equal slices, and each one the whole height of the bar, so a
        // tap anywhere in the column lands on it.
        'b4-focus relative flex-1 min-w-0 min-h-[60px] flex flex-col items-center justify-center gap-1 px-0.5 pt-2 pb-1.5',
        // Sideways the label moves next to the icon instead of under it, which
        // is the whole difference between a 60px bar and a 40px one.
        'short:min-h-[40px] short:flex-row short:gap-1.5 short:py-1',
        'text-[length:calc(12px*var(--b4-type,1))] font-medium transition-colors duration-[120ms]',
        isActive ? 'text-white' : 'text-navy-300 hover:text-navy-100',
      ].join(' ')}
    >
      {/* Where you are, drawn on the bar's top rule rather than as a pill
          behind the tab: the green is the one colour here that means "this". */}
      {isActive ? (
        <span aria-hidden="true" className="absolute -top-px left-1/2 -translate-x-1/2 w-7 h-[3px] rounded-b-sm bg-state-ok" />
      ) : null}
      <Icon size={20} className="short:w-4 short:h-4" />
      <span className="max-w-full truncate">{title}</span>
    </Link>
  );
};

export default MyApp;
